// Gate B6, impact and VFX (engine/docs/spikes/002-green-valley/brief.md), now on @rpg-unit/impact-feedback (the impact
// director, cut from this file; decision 0077). The feature listens to the standard `impact` event (@rpg/contracts),
// which the core's adapter emits from `combat:hit` and `combat:defeated` (the "finisher" kind), and fans each out by
// its kind, as the impact-cues table says (@rpg-unit/impact-cue-presets, loaded into the registry), to its channels:
// hit-stop, the struck body's flash, camera shake and kick, particles, sound (phase D2's audio through
// `setImpactSound`), and rumble with no sink (contradiction 1). What stays here is the game's side:
//   - its values from the content: the cue rows, the bursts they name (`impact-emitters`), the telegraph styles (the
//     game's `telegraph-styles` table, which combat reads too) and the feedback row;
//   - its own combat events into the feature's inputs: `combat:move` opens and closes a weapon trail
//     (`moveWindow`), `combat:telegraph` draws the move's danger zone (`telegraph`);
//   - the player's settings (the shake slider, reduce motion resolved against the OS, the flash cap the accessibility
//     settings write);
//   - the planted defect's switch in the spike's one faults record.
//
// Planted defect `impact-channel-unregistered` (the feature's IMPACT_FEEDBACK_FAULTS.channelUnregistered): the flash
// channel is never registered; the cue spec's per-kind count for it must go red (and the router reports it missing).
import type * as THREE from "three";
import type { EventBus } from "@rpg/kernel";
import type { CameraRig, ShakeSettings } from "@rpg/camera";
import type { ImpactEvent as StandardImpact } from "@rpg/contracts";
import type { Runtime } from "@rpg/runtime";
import type { CueTable, EmitterSpec, TelegraphStyle, WeaponSample } from "@rpg/vfx";
import { cueTable, emitterSpecs, type FeedbackPreset, type ImpactCuePreset, type ImpactEmitterPreset } from "@rpg-unit/impact-cue-presets";
import { applyImpactFeedbackFaults, IMPACT_FEEDBACK_FAULTS, mountImpactFeedback, type ImpactFeedback } from "@rpg-unit/impact-feedback";
import type { SpikeContent } from "../content";
import type { FeedbackRow } from "../content/types.gen";
import type { GameEvents } from "../core";
import type { FaultGroup } from "../faults";

export { CHANNELS, SINKLESS, UNREGISTERED_CHANNEL } from "@rpg-unit/impact-feedback";

/** The planted defects this module owns, by their `?faults=` names. */
export const FEEDBACK_FAULT_NAMES = ["impact-channel-unregistered"] as const;
const readSwitch = (search: string): void => {
  if ((new URLSearchParams(search).get("faults") ?? "").split(",").includes("impact-channel-unregistered")) IMPACT_FEEDBACK_FAULTS.channelUnregistered = true;
};

/** What the sound channel and the audio get: the standard impact. */
export type ImpactEvent = StandardImpact;

/** Phase D2: where the sound channel sends its cue (src/audio/ sets it; until then the cues are only recorded). */
let impactSound: ((cue: string, e: ImpactEvent) => void) | null = null;
/** Route the sound channel's cues to the audio (null to stop). */
export function setImpactSound(fn: ((cue: string, e: ImpactEvent) => void) | null): void {
  impactSound = fn;
}
/** Phase D2 (decision 0063): the flash cap in force, flashes allowed to start per real second (the accessibility
 *  setting writes it; every mounted director reads it each frame). */
export const FLASH_CAP = { perSecond: Infinity };

/** The fault group for the spike's one faults record; reads `?faults=` on creation. */
export function feedbackFaultGroup(search = typeof location !== "undefined" ? location.search : ""): FaultGroup {
  readSwitch(search);
  return {
    names: FEEDBACK_FAULT_NAMES,
    get: () => IMPACT_FEEDBACK_FAULTS.channelUnregistered,
    set: (_n, v) => {
      IMPACT_FEEDBACK_FAULTS.channelUnregistered = v;
      applyImpactFeedbackFaults();
    },
  };
}

/** The tables as the engine's types. */
export function feedbackValues(content: SpikeContent): {
  cues: CueTable;
  emitters: Record<string, EmitterSpec>;
  styles: Record<string, TelegraphStyle>;
  shake: ShakeSettings;
  row: FeedbackRow;
} {
  const reg = content.registry;
  const cues = cueTable(reg.rows("impact-cues") as unknown as readonly ImpactCuePreset[]);
  const emitters = emitterSpecs(reg.rows("impact-emitters") as unknown as readonly ImpactEmitterPreset[]);
  const styles: Record<string, TelegraphStyle> = {};
  for (const s of reg.rows("telegraph-styles")) {
    const { id, reason: _r, ...style } = s;
    styles[id] = style;
  }
  const row = reg.get("feedback", "default") as FeedbackRow | undefined;
  if (!row) throw new Error("feedback: the feedback table has no 'default' row");
  return { cues, emitters, styles, shake: row.shake, row };
}

/** What the director needs from the scene it serves. */
export interface FeedbackDeps {
  runtime: Runtime;
  /** The kernel game's event bus (the impacts and combat's move events arrive here, in the sim step they happen). */
  events: EventBus<GameEvents>;
  /** Where the particles, telegraphs and trails are drawn. */
  scene: THREE.Object3D;
  content: SpikeContent;
  /** The camera's rig (its shake and kick); null for a scene without one. */
  rig: CameraRig | null;
  /** Where a weapon's base and tip are now (world space), for trails. */
  weaponOf: WeaponSample;
  /** The ground's height (telegraphs drape on it). */
  heightAt: (x: number, z: number) => number;
  /** The player's settings now: the shake slider (0..1) and reduce motion (resolved against the OS). */
  motion: () => { shake: number; reduceMotion: boolean };
}

/** The mounted director: the feature's. */
export type Feedback = ImpactFeedback;

/** Mount the director on a scene. */
export function mountFeedback(d: FeedbackDeps): Feedback {
  const v = feedbackValues(d.content);
  if (typeof location !== "undefined") readSwitch(location.search);
  const f = mountImpactFeedback({
    runtime: d.runtime,
    events: d.events,
    scene: d.scene,
    values: { cues: v.cues, emitters: v.emitters, styles: v.styles, feel: v.row as FeedbackPreset },
    rig: d.rig,
    weaponOf: d.weaponOf,
    heightAt: d.heightAt,
    motion: d.motion,
    sound: (cue, e) => impactSound?.(cue, e),
    flashCap: () => FLASH_CAP.perSecond,
  });
  // the game's own combat events into the feature's inputs
  const offs = [d.events.on("combat:move", (m) => f.moveWindow(m)), d.events.on("combat:telegraph", (t) => f.telegraph(t))];
  const dispose = f.dispose;
  return {
    ...f,
    dispose() {
      offs.forEach((o) => o());
      dispose();
    },
  };
}

/** Reduce motion as the settings screen leaves it ("system" follows the OS, decision 0063). */
export function reduceMotionOf(value: unknown): boolean {
  if (value === "on") return true;
  if (value === "off") return false;
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}
